import React from "react";

const DEFAULT_VISIBLE_PAGES = 5;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

/**
 * Bootstrap Pagination Button
 */
const PaginationButton = ({
  pageNumber,
  pageText,
  disabled = false,
  active = false,
  onClick,
  href,
  itemClass,
  linkClass,
  activeClass,
  activeLinkClass,
  disabledClass,
}) => {

  const handleClick = (e) => {
    e.preventDefault();

    if (!disabled && typeof onClick === "function") {
      onClick(pageNumber);
    }
  };

  const liClass = [
    itemClass,
    active ? activeClass : "",
    disabled ? disabledClass : "",
  ]
    .filter(Boolean)
    .join(" ");

  const aClass = [
    linkClass,
    active ? activeLinkClass : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <li className={liClass}>
      <a
        href={href || "#"}
        className={aClass}
        onClick={handleClick}
        aria-current={active ? "page" : undefined}
      >
        {pageText}
      </a>
    </li>
  );
};

const Pagination = (props) => {

  const {
    totalItemsCount = 0,
    itemsCountPerPage = 10,
    activePage = 1,
    onChange,

    firstPageText = "\u00AB",
    prevPageText = "\u2039",
    nextPageText = "\u203A",
    lastPageText = "\u00BB",

    hideNavigation = false,
    hideFirstLastPages = false,
    hideDisabled = false,

    disabledClass = "disabled",
    activeClass = "active",

    innerClass = "pagination",

    itemClass,
    itemClassFirst,
    itemClassPrev,
    itemClassNext,
    itemClassLast,

    linkClass,
    linkClassFirst,
    linkClassPrev,
    linkClassNext,
    linkClassLast,

    activeLinkClass,

    renderOnZeroPageCount,

    getPageUrl = () => "#",
  } = props;

  const totalPages = Math.ceil(
    (totalItemsCount || 0) /
      Math.max(itemsCountPerPage || 1, 1)
  );

  if (totalPages <= 0) {

    if (typeof renderOnZeroPageCount === "function") {
      return renderOnZeroPageCount(props);
    }

    return null;
  }

  const currentPage = clamp(
    activePage,
    1,
    totalPages
  ); 

    /*
   * ==========================================================
   * EXACT react-js-pagination page window
   *
   * Page 1  -> 1 2 3 4 5
   * Page 2  -> 2 3 4 5 6
   * Page 3  -> 3 4 5 6 7
   *
   * Last pages
   *
   * 86 pages
   * page 84 -> 82 83 84 85 86
   * page 85 -> 82 83 84 85 86
   * page 86 -> 82 83 84 85 86
   * ==========================================================
   */

  const visiblePages = DEFAULT_VISIBLE_PAGES;
let startPage = 1;

if (totalPages <= visiblePages) {
  startPage = 1;
} else if (currentPage <= 3) {
  // Keep first 5 pages visible
  startPage = 1;
} else if (currentPage >= totalPages - 2) {
  // Keep last 5 pages visible
  startPage = totalPages - visiblePages + 1;
} else {
  // Start sliding after page 3
  startPage = currentPage - 2;
}

if (startPage < 1) {
  startPage = 1;
}

  let endPage = Math.min(
    startPage + visiblePages - 1,
    totalPages
  );

  const pageNumbers = [];

  for (let page = startPage; page <= endPage; page++) {
    pageNumbers.push(page);
  }

  const hasPreviousPage = currentPage > 1;
  const hasNextPage = currentPage < totalPages;

  const items = [];

  /*
   * <<
   */

  if (!hideFirstLastPages && (!hideDisabled || hasPreviousPage)) {

    items.push(
      <PaginationButton
        key="first"
        pageNumber={1}
        pageText={firstPageText}
        disabled={!hasPreviousPage}
        onClick={onChange}
        href={getPageUrl(1)}
        itemClass={[itemClass, itemClassFirst]
          .filter(Boolean)
          .join(" ")}
        linkClass={[linkClass, linkClassFirst]
          .filter(Boolean)
          .join(" ")}
        activeClass={activeClass}
        activeLinkClass={activeLinkClass}
        disabledClass={disabledClass}
      />
    );

  }

  /*
   * <
   */

  if (!hideNavigation && (!hideDisabled || hasPreviousPage)) {

    items.push(
      <PaginationButton
        key="prev"
        pageNumber={currentPage - 1}
        pageText={prevPageText}
        disabled={!hasPreviousPage}
        onClick={onChange}
        href={getPageUrl(currentPage - 1)}
        itemClass={[itemClass, itemClassPrev]
          .filter(Boolean)
          .join(" ")}
        linkClass={[linkClass, linkClassPrev]
          .filter(Boolean)
          .join(" ")}
        activeClass={activeClass}
        activeLinkClass={activeLinkClass}
        disabledClass={disabledClass}
      />
    );

  }

  /*
   * Page Numbers
   */

  pageNumbers.forEach((page) => {

    items.push(

      <PaginationButton
        key={page}
        pageNumber={page}
        pageText={page}
        active={page === currentPage}
        disabled={false}
        onClick={onChange}
        href={getPageUrl(page)}
        itemClass={itemClass}
        linkClass={linkClass}
        activeClass={activeClass}
        activeLinkClass={activeLinkClass}
        disabledClass={disabledClass}
      />

    );

  }); 
    /*
   * >
   */

  if (!hideNavigation && (!hideDisabled || hasNextPage)) {

    items.push(
      <PaginationButton
        key="next"
        pageNumber={currentPage + 1}
        pageText={nextPageText}
        disabled={!hasNextPage}
        onClick={onChange}
        href={getPageUrl(currentPage + 1)}
        itemClass={[itemClass, itemClassNext]
          .filter(Boolean)
          .join(" ")}
        linkClass={[linkClass, linkClassNext]
          .filter(Boolean)
          .join(" ")}
        activeClass={activeClass}
        activeLinkClass={activeLinkClass}
        disabledClass={disabledClass}
      />
    );

  }

  /*
   * >>
   */

  if (!hideFirstLastPages && (!hideDisabled || hasNextPage)) {

    items.push(
      <PaginationButton
        key="last"
        pageNumber={totalPages}
        pageText={lastPageText}
        disabled={!hasNextPage}
        onClick={onChange}
        href={getPageUrl(totalPages)}
        itemClass={[itemClass, itemClassLast]
          .filter(Boolean)
          .join(" ")}
        linkClass={[linkClass, linkClassLast]
          .filter(Boolean)
          .join(" ")}
        activeClass={activeClass}
        activeLinkClass={activeLinkClass}
        disabledClass={disabledClass}
      />
    );

  }

  return (
    <ul className={innerClass}>
      {items}
    </ul>
  );

};

export default Pagination;